import React, { useState, useEffect } from "react";
import apiClient from "../api/apiClient";
import { useParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";

const ServicesDetails = () => {
  const { id } = useParams();
  const [service, setService] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [otherServices, setOtherServices] = useState([]);
  const [otherLoading, setOtherLoading] = useState(true);
  const [otherError, setOtherError] = useState(null);

  // Fetch service details
  useEffect(() => {
    const fetchServiceDetail = async () => {
      try {
        setLoading(true);
        const response = await apiClient.get(`/services/${id}`);
        setService(response.data);
        setError(null);
      } catch (err) {
        setError(
          "Impossible de charger le détail du service. Veuillez réessayer plus tard.",
        );
      } finally {
        setLoading(false);
      }
    };
    fetchServiceDetail();
  }, [id]);

  // Fetch other services
  useEffect(() => {
    const fetchOtherServices = async () => {
      try {
        setOtherLoading(true);
        const response = await apiClient.get(`/services`);
        const filtered = response.data.filter(
          (s) => s.id_service !== Number(id),
        );
        setOtherServices(filtered);
        setOtherError(null);
      } catch (err) {
        console.error(
          "Erreur lors de la récupération des autres services :",
          err,
        );
        setOtherError("Impossible de charger les autres services.");
      } finally {
        setOtherLoading(false);
      }
    };
    fetchOtherServices();
  }, [id]);

  if (loading) return <p>Chargement du détail du service...</p>;
  if (error) return <p className="text-danger">{error}</p>;

  // JSON-LD structured data for the service
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Service",
    name: service.titre,
    description: service.description,
    image: service.image_service
      ? `/uploads/${service.image_service}`
      : "https://dvelgegroup.com/images/placeholder.png",
    provider: {
      "@type": "Organization",
      name: "DVELGE GROUP",
      url: "https://dvelgegroup.com",
      logo: "https://dvelgegroup.com/images/logo.png",
      contactPoint: {
        "@type": "ContactPoint",
        telephone: "+224627020871",
        contactType: "Customer Service",
      },
    },
  };

  return (
    <div>
      <Helmet>
        <title>{service.titre} | DVELGE GROUP</title>
        <meta name="description" content={service.description} />
        <meta property="og:type" content="website" />
        <meta property="og:title" content={`${service.titre} | DVELGE GROUP`} />
        <meta property="og:description" content={service.description} />
        <meta
          property="og:url"
          content={`https://dvelgegroup.com/servicesDetails/${id}`}
        />
        <meta
          property="og:image"
          content={
            service.image_service
              ? `/uploads/${service.image_service}`
              : "https://dvelgegroup.com/images/placeholder.png"
          }
        />
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content={`${service.titre} | DVELGE GROUP`}
        />
        <meta name="twitter:description" content={service.description} />
        <meta
          name="twitter:image"
          content={
            service.image_service
              ? `/uploads/${service.image_service}`
              : "https://dvelgegroup.com/images/placeholder.png"
          }
        />
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>

      {/* Service Hero Section */}
      <section className="page-heading-sec r-bg-a pt85 pb120">
        <div className="container">
          <div className="row pt80 justify-content-center">
            <div className="col-lg-6">
              <div className="page-headings">
                <span
                  className="sub-heading mb15"
                  data-aos="fade-up"
                  data-aos-delay="200"
                >
                  Nos Services
                </span>
                <h1 data-aos="fade-up" data-aos-delay="400">
                  {service.titre}{" "}
                  <span className="ree-text rt45">DVELGE GROUP</span>
                </h1>
                <p
                  className="mt20 h-light"
                  data-aos="fade-up"
                  data-aos-delay="400"
                >
                  {service.description}
                </p>
              </div>
            </div>
            <div className="col-lg-6">
              <div
                className="sol-img m-mt30"
                data-aos="fade-in"
                data-aos-delay="400"
              >
                <img
                  src={
                    service.image_service
                      ? `${process.env.REACT_APP_API_URL}/uploads/${service.image_service}`
                      : "images/placeholder.png"
                  }
                  alt={service.titre}
                  className="img-fluid"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Other Services Section */}
      <section className="r-bg-a sec-pad">
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-lg-7 text-center">
              <div className="page-headings">
                <h2>Autres services</h2>
                <p className="mt15">
                  {" "}
                  Découvrez d'autres services que nous proposons{" "}
                </p>
              </div>
            </div>
          </div>

          {otherLoading ? (
            <p>Chargement des autres services...</p>
          ) : otherError ? (
            <p className="text-danger">{otherError}</p>
          ) : (
            <div className="row mt30">
              {otherServices.map((otherService) => (
                <div
                  className="col-lg-4 col-sm-6"
                  key={otherService.id_service}
                >
                  <div className="ree-card mt60">
                    <div className="ree-card-img">
                      <img
                        src={`${process.env.REACT_APP_API_URL}/uploads/${otherService.image_service}`}
                        alt={otherService.titre}
                      />
                    </div>
                    <div className="ree-card-content mt40">
                      <h3 className="mb15">{otherService.titre}</h3>
                      <p>{otherService.description}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Contact Call-to-Action */}
      <section className="sec-pad r-bg-a">
        <div className="container">
          <div className="row">
            <div className="col-lg-6 vcenter">
              <div className="cta-heading">
                <span className="sub-heading mb15">Contactez-nous</span>
                <h2>Vous avez un projet en tête ? Parlons-en</h2>
                <a
                  href="tel:+224627020871"
                  className="ree-btn ree-btn-grdt1 mt40 mr20"
                >
                  <i className="fas fa-phone-alt fb-bigon"></i> Contact
                </a>
                <a
                  href="https://wa.me/+224627020871"
                  className="ree-btn whatsapp-bg mt40 shadows"
                >
                  <i className="fab fa-whatsapp fb-bigon"></i> Whatsapp
                </a>
              </div>
            </div>
            <div className="col-lg-6 vcenter text-center">
              <img
                src="images/cta/idea.jpg"
                alt="working with client"
                className="img-fluid"
              />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ServicesDetails;
